import { createReducer } from "@reduxjs/toolkit";
import moment from "moment";
import * as actionTypes from "~/store/action_types";
import { calculateElapsed } from "~/utils/date.jsx";
import { NotFoundError } from "~/utils/errors";

const setElapsedTime = (oldStopwatch, offset = 0) => {
  const started_on = oldStopwatch.started_on;
  const elapsed = oldStopwatch.elapsed;
  oldStopwatch.elapsed = calculateElapsed(started_on, elapsed, offset);
  oldStopwatch.started_on = null;
};

const stopwatches = createReducer({}, (builder) => {
  builder
    .addCase(actionTypes.STOPWATCH_START_LOADING, (state, action) => {
      const { parent_type, parent_id, elapsed, request_offset } = action;
      state[`${parent_type}-${parent_id}`] = {
        id: 0,
        started_on: elapsed > 0 ? null : moment().utc().unix() - parseInt(request_offset),
        created_on: moment().unix(),
        parent_type,
        parent_id,
        elapsed,
      };
    })
    .addCase(actionTypes.STOPWATCH_START_ERROR, (state, action) => {
      const { parent_type, parent_id } = action;
      delete state[`${parent_type}-${parent_id}`];
    })
    .addCase(actionTypes.STOPWATCH_PAUSE_ERROR, (state, action) => {
      const { id, error } = action;
      if (error instanceof NotFoundError) {
        delete state[`${id}`];
      }
      return;
    })
    .addCase(actionTypes.STOPWATCH_RESUME_SUCCESS, (state, action) => {
      const { id, response = {} } = action;
      const { entities = {} } = response;
      const { stopwatches = {} } = entities;
      const nextState = stopwatches[`${id}`] || {};

      if (state[`${id}`].started_on !== null) {
        state[`${id}`] = { ...state[`${id}`], ...nextState };
      }
    })
    .addCase(actionTypes.STOPWATCH_PAUSE_SUCCESS, (state, action) => {
      const { response = {} } = action;
      const { result = {}, entities = {} } = response;
      const { stopwatches = {} } = entities;
      const { single: id } = result;
      const nextState = stopwatches[`${id}`] || {};
      localStorage.removeItem(`stopwatch-notification-${id}`);
      state[`${id}`] = { ...state[`${id}`], ...nextState };
    })
    .addCase(actionTypes.STOPWATCH_PAUSE_LOADING, (state, action) => {
      const { id } = action;
      state[`${id}`].started_on = null;
    })
    .addCase(actionTypes.STOPWATCHES_FETCH_SUCCESS, (state, action) => {
      const { response = {} } = action;
      const { entities = {} } = response;
      const { stopwatches = {} } = entities;
      state = stopwatches;
      return state;
    })
    .addCase(actionTypes.STOPWATCH_RESUME_ERROR, (state, action) => {
      const { id, error, startedOn } = action;
      if (error instanceof NotFoundError) {
        delete state[`${id}`];
      }
      state[`${id}`].started_on = startedOn;
    })
    .addCase(actionTypes.STOPWATCH_RESUME_LOADING, (state, action) => {
      const { id, error, request_offset = 0 } = action;
      const keys = Object.keys(state);
      keys.forEach((key) => {
        if (state[`${key}`].started_on) {
          setElapsedTime(state[`${key}`], request_offset);
        }
      });

      if (error instanceof NotFoundError) {
        delete state[`${id}`];
      }
      state[`${id}`].started_on = moment().utc().unix() - parseInt(request_offset);
    })
    .addCase(actionTypes.STOPWATCH_MERGE_SUCCESS, (state, action) => {
      const { response = {}, unassignedStopwatchId = 0 } = action;
      const { result = {}, entities = {} } = response;
      const { stopwatches = {} } = entities;
      const { single: id } = result;
      const nextState = stopwatches[`${id}`] || {};
      state[`${id}`] = { ...state[`${id}`], ...nextState };
      if (unassignedStopwatchId) {
        delete state[`${unassignedStopwatchId}`];
      }
    })
    .addMatcher(
      (action) =>
        [actionTypes.STOPWATCH_UPDATED_FROM_EVENT_STREAM, actionTypes.STOPWATCH_EDIT_SUCCESS].includes(action.type),
      (state, action) => {
        const { response = {} } = action;
        const { result = {}, entities = {} } = response;
        const { stopwatches = {} } = entities;
        const { single: id } = result;
        const nextState = stopwatches[`${id}`] || {};
        if (
          action.type === actionTypes.STOPWATCH_UPDATED_FROM_EVENT_STREAM &&
          nextState?.updated_on < state[`${id}`]?.updated_on
        ) {
          return state;
        }
        state[`${id}`] = { ...state[`${id}`], ...nextState };
      }
    )
    .addMatcher(
      (action) =>
        [actionTypes.STOPWATCH_STARTED_FROM_EVENT_STREAM, actionTypes.STOPWATCH_START_SUCCESS].includes(action.type),
      (state, action) => {
        const { response = {}, elapsed = 0, request_offset = 0 } = action;
        const { result = {}, entities = {} } = response;
        const { stopwatches = {} } = entities;
        const { single: id } = result;
        const { parent_type, parent_id } = action;
        delete state[`${parent_type}-${parent_id}`];
        const nextState = stopwatches[`${id}`] || {};

        if (elapsed === 0) {
          const keys = Object.keys(state);
          keys.forEach((key) => {
            if (state[`${key}`].started_on) {
              setElapsedTime(state[`${key}`], request_offset);
            }
          });
        }

        state[`${id}`] = { ...state[`${id}`], ...nextState };
      }
    )
    .addMatcher(
      (action) =>
        [actionTypes.STOPWATCH_DELETED_FROM_EVENT_STREAM, actionTypes.STOPWATCH_DELETE_SUCCESS].includes(action.type),
      (state, action) => {
        const { id } = action;
        localStorage.removeItem(`stopwatch-notification-${id}`);
        delete state[`${id}`];
      }
    )
    .addDefaultCase((state) => {
      return state;
    });
});

export default stopwatches;
